/**
 * @file analytics.js
 * @description Single funnel-event entry point for the acquisition surfaces
 *   (homepage, SEO landing pages, signup). Named conversion events flow through
 *   `track()` so every call site is identical regardless of where the event ends
 *   up. Two sinks, layered in later slices:
 *     • Supabase `events` (in-house, SOURCE OF TRUTH) — guest/anon aware, full
 *       detail. Wired in Slice 2A.
 *     • GA4 (marketing) — same event NAMES, non-PII params only. Wired in 2B,
 *       gated on REACT_APP_GA4_ID.
 *
 *   This seed is intentionally no-op-safe: it never throws and, until 2A/2B add
 *   the sinks, only forwards to GA4 if a gtag happens to be present. Call sites
 *   added in Slice 1 therefore work immediately and light up once the sinks land.
 * @module lib/analytics
 */

import { trackNamedEvent } from "./eventService";
import { sendGa4 } from "./ga4";
import { getUtmContent } from "./attribution";

/** Named conversion events (the 10 from the acquisition plan). */
export const A = {
  HOMEPAGE_FIND_CASES_CLICKED:  "homepage_find_cases_clicked",
  HOMEPAGE_TRACKER_CLICKED:     "homepage_tracker_clicked",
  HOMEPAGE_CAROUSEL_CASE_APPLY_CLICKED: "homepage_carousel_case_apply_clicked",
  // The hero's single "Just posted" case card (card or its "See this case" button).
  // Carousel cards keep the event above; both carry placement + browsing state.
  HOMEPAGE_HERO_CASE_CLICKED:   "homepage_hero_case_clicked",
  HOMEPAGE_CAROUSEL_AGENCY_CTA_CLICKED: "homepage_carousel_agency_cta_clicked",
  TRACKER_SIGNUP_STARTED:       "tracker_signup_started",
  TRACKER_SIGNUP_COMPLETED:     "tracker_signup_completed",
  CASE_ALERT_SIGNUP_STARTED:    "case_alert_signup_started",
  CASE_ALERT_SIGNUP_COMPLETED:  "case_alert_signup_completed",
  CASE_FINDER_OPENED:           "case_finder_opened",
  // Apply through signup (owner + David 2026-10-23). A signed-out visitor tapped Apply (or Save / Pass, or the
  // unlock button) on a case; metadata: case_id, case_code, action. Then APPLY_RESUMED fires when that case reopens
  // with the application form in view after signup and onboarding (metadata: case_id). Admin > Analytics reads the
  // funnel per case: signed-out views, these taps, signups from the case, applications.
  GUEST_APPLY_CLICK:            "guest_apply_click",
  APPLY_RESUMED:                "apply_resumed",
  // Sign-up and setup funnel (owner 2026-10-29: 7 of 7 guests who tapped Apply left at sign-up, 21 of 63 new providers
  // never finished setup, and nothing recorded where). Sent only through lib/signupFunnel.js; no PII in metadata.
  SIGNUP_FORM_VIEWED:           "signup_form_viewed",
  SIGNUP_SUBMITTED:             "signup_submitted",
  SIGNUP_FAILED:                "signup_failed",
  SIGNUP_SUCCEEDED:             "signup_succeeded",
  VERIFY_LOGIN_PROMPT:          "verify_login_prompt",
  ONBOARDING_STEP_VIEWED:       "onboarding_step_viewed",
  ONBOARDING_FINISHED:          "onboarding_finished",
  APPLY_RESUME_MISSED:          "apply_resume_missed",
  // A signed-out visitor's screen reached the Apply button on a shared case page (lib/caseScrollTracking). Once per
  // case per page load; metadata: case_id, case_code, depth "apply_button", on_first_screen (visible without
  // scrolling). Compared with case_view, answers whether guests from social posts reach the Apply button.
  CASE_SCROLL:                  "case_scroll",
  // Fires when a therapist search returns NOTHING. The most important moment
  // on the platform while supply is thin, and previously invisible. Carries
  // hint_offered so zero-result-with-nothing-within-100mi can be told apart
  // from zero-result-because-the-radius-was-tight.
  SEARCH_ZERO_RESULTS:          "search_zero_results",
  // A provider typed a case ID (RBT-1495, BCBA-1445) into Find Cases, usually
  // from an Instagram case card. Metadata: outcome (open | closed | not_found |
  // error), case_code. Measures social card -> site conversions per card.
  CASE_ID_SEARCH:               "case_id_search",
  CITY_PAGE_CASE_ALERT_CLICKED: "city_page_case_alert_clicked",
  CITY_PAGE_TRACKER_CLICKED:    "city_page_tracker_clicked",
  AGENCY_POST_CASE_CLICKED:     "agency_post_case_clicked",
  // Agency-acquisition landing pages (/post-a-case, /post-aba-cases): one view per page
  // load, carrying utm_content (the outreach prospect id) so email clicks are measurable.
  // The CTA click on those pages is AGENCY_SIGNUP_CLICK (same meta + utm_content).
  AGENCY_LANDING_VIEWED:        "agency_landing_viewed",

  // Phase 4A — SEO/CTA conversion coverage. Added specifically for CTA click
  // sites that had no tracking at all (see docs/seo-phase-4a-indexation-analytics-qa.md
  // for the full audit). Pages that already fired an equivalent event above
  // (e.g. CITY_PAGE_CASE_ALERT_CLICKED, AGENCY_POST_CASE_CLICKED) were left as-is
  // rather than double-firing two events per click.
  PROVIDER_SIGNUP_CLICK:   "provider_signup_click",
  AGENCY_SIGNUP_CLICK:     "agency_signup_click",
  CASE_ALERT_CLICK:        "case_alert_click",
  TRACKER_SIGNUP_CLICK:    "tracker_signup_click",
  RESOURCE_PAGE_CTA_CLICK: "resource_page_cta_click",
  LANGUAGE_SWITCH_CLICK:   "language_switch_click",
  FOOTER_LINK_CLICK:       "footer_link_click",
  NAV_LINK_CLICK:          "nav_link_click",

  // Provider profile share card (canvas-rendered social image, see
  // components/ProfileShareCard.jsx). Metadata is role/state only — never the
  // provider's name, credential ID, or any other identifying field.
  PROVIDER_SHARE_CARD_OPENED:          "provider_share_card_opened",
  PROVIDER_SHARE_CARD_GENERATED:       "provider_share_card_generated",
  PROVIDER_SHARE_CARD_DOWNLOADED:      "provider_share_card_downloaded",
  PROVIDER_SHARE_CAPTION_COPIED:       "provider_share_caption_copied",
};

/**
 * Build the consistent metadata shape for Phase 4A CTA/nav/footer events, so
 * every one of the ~30 new call sites reports the same fields instead of each
 * hand-rolling a slightly different object. Never includes PII — only route
 * identifiers, labels, and low-cardinality dimensions (mirrors the existing
 * ga4Params non-PII rule below).
 * @param {Object} p
 * @param {string} p.pagePath    current route, e.g. "/aba-resources/"
 * @param {string} p.pageType    "provider" | "agency" | "tracker" | "resource" | "nav" | "footer"
 * @param {string} p.ctaLabel    visible button/link text
 * @param {string} p.targetPath  where the click navigates to
 * @param {string} [p.userLanguage]  "en" | "es" — current i18n.language
 * @param {string} [p.market]    "miami" | "charlotte" | "north-carolina" — only when the page is market-specific
 * @param {string} [p.audience]  "provider" | "agency" | "tracker" | "resource"
 */
export function ctaMeta({ pagePath, pageType, ctaLabel, targetPath, userLanguage, market, audience }) {
  return {
    page_path: pagePath,
    page_type: pageType,
    cta_label: ctaLabel,
    target_path: targetPath,
    user_language: userLanguage,
    ...(market ? { market } : {}),
    audience,
  };
}

/**
 * Supabase metadata for an agency-landing event: the page `source` plus the outreach
 * prospect id (utm_content) when present. Never GA4 (the prospect id stays in-house).
 * @param {string} source  e.g. "post_a_case" | "post_aba_cases"
 * @param {Object} [extra] additional low-cardinality fields (e.g. cta_position)
 */
export function landingMeta(source, extra = {}) {
  const utm_content = getUtmContent();
  return { source, ...(utm_content ? { utm_content } : {}), ...extra };
}

const _landingViewsSent = new Set();

/**
 * Record an agency landing-page view ONCE per page load (a module-level guard also
 * absorbs React StrictMode's double effect and re-mounts within the same load).
 * @param {string} source  "post_a_case" | "post_aba_cases"
 */
export function trackLandingView(source) {
  if (_landingViewsSent.has(source)) return;
  _landingViewsSent.add(source);
  track(A.AGENCY_LANDING_VIEWED, {
    supabaseMeta: landingMeta(source, { page_path: typeof window !== "undefined" ? window.location.pathname : null }),
    ga4Params: { page_type: "agency", audience: "agency", source },
  });
}

/** Test-only: reset the once-per-load guard. */
export function _resetLandingViewsForTest() { _landingViewsSent.clear(); }

/**
 * Fire a named conversion event.
 * @param {string} name  one of A.*
 * @param {Object} [opts]
 * @param {Object} [opts.supabaseMeta]  full-detail metadata for the in-house sink (Slice 2A)
 * @param {Object} [opts.ga4Params]     non-PII params for GA4 (Slice 2B)
 */
export function track(name, opts = {}) {
  if (!name) return;
  // In-house Supabase sink (SOURCE OF TRUTH) — guest/anon aware, full detail.
  try {
    trackNamedEvent(name, { userId: opts.userId || null, metadata: opts.supabaseMeta || {} });
  } catch {
    /* analytics must never break a click */
  }
  // GA4 sink (marketing) — name + non-PII params only (lib/ga4.js loads gtag when
  // REACT_APP_GA4_ID is set; this forwards only when a gtag is present).
  trackGa4Only(name, opts.ga4Params || {});
}

/**
 * Fire an event to GA4 ONLY — no Supabase write. Use this for events that are
 * ALREADY recorded in the in-house `events` table by another call (e.g. the
 * lifecycle `sign_up`/`login` written via eventService.trackEvent) but that GA4
 * also needs — routing them through track() would double-write the Supabase row
 * and inflate the Admin funnel. Non-PII params only. Never throws.
 * @param {string} name  GA4 event name (prefer GA4-recommended names like "sign_up")
 * @param {Object} [params]  non-PII params (e.g. { method: "email" })
 */
export function trackGa4Only(name, params = {}) {
  sendGa4(name, params);
}
